import { safeSetItem } from "@/lib/storage/safe-local";
import { useEffect, useState } from "react";
import { track } from "@/lib/customerio/track";
import { CIO_EVENTS } from "@/lib/customerio/events";

/** One fragrance bottle inside a composed pack line. */
export type PackCartBottle = {
  productId: string;
  slug: string;
  name: string;
  qty: number;
  unitPrice: number;
};

/** Composition metadata carried by a pack cart line (see lib/packs/cart.ts). */
export type PackCartMeta = {
  packId: string;
  packSlug: string;
  packName: string;
  variantId: string;
  variantLabel: string;
  bottleSizeMl: number;
  bottleCount: number;
  bottlesPerBox: number;
  subtotal: number;
  discount: number;
  bottles: PackCartBottle[];
  /** Gift/corporate metadata. Absent on Discovery pack lines. */
  packCategory?: string;
  compositionMode?: string;
  /** Curated contents of a fixed or hybrid gift pack. */
  fixedItems?: PackCartFixedItem[];
  /** Paid extras chosen with the pack. */
  addons?: PackCartAddon[];
  addonsTotal?: number;
  gift?: PackCartGift;
  /** Pack-specific free Standard Delivery (pack pricing_rules.freeShipping); re-derived server-side. */
  freeShipping?: boolean;
};

export type PackCartFixedItem = {
  itemType: string;
  label: string;
  qty: number;
  sizeMl: number | null;
};

export type PackCartAddon = {
  addonId: string;
  addonType: string;
  label: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
};

export type PackCartGift = {
  recipientName: string;
  senderName: string;
  message: string;
  hidePrices: boolean;
};

export type CartLine = {
  productId: string;
  slug: string;
  name: string;
  variantId: string;
  variantLabel: string;
  unitPrice: number;
  quantity: number;
  img: string;
  /** Present only for Pack Builder lines; regular products leave this unset. */
  pack?: PackCartMeta;
};

const KEY = "cb16.cart";
const EVT = "cb16:cart";
const OPEN_EVT = "cb16:cart:open";

function read(): CartLine[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(lines: CartLine[]) {
  if (typeof window === "undefined") return;
  try {
    safeSetItem(KEY, JSON.stringify(lines));
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {
    /* ignore */
  }
}

function lineKey(l: Pick<CartLine, "productId" | "variantId">) {
  return `${l.productId}:${l.variantId}`;
}

export function addToCart(line: CartLine) {
  const lines = read();
  const key = lineKey(line);
  const idx = lines.findIndex((l) => lineKey(l) === key);
  if (idx >= 0) {
    lines[idx] = { ...lines[idx], quantity: lines[idx].quantity + line.quantity };
  } else {
    lines.push(line);
  }
  write(lines);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_EVT));
  }
  track(CIO_EVENTS.ADD_TO_CART, {
    product_id: line.productId,
    product_slug: line.slug,
    product_name: line.name,
    variant: line.variantLabel,
    quantity: line.quantity,
    unit_price: line.unitPrice,
    currency: "INR",
  });
}

export function updateQuantity(productId: string, variantId: string, quantity: number) {
  const lines = read();
  const key = `${productId}:${variantId}`;
  const before = lines.find((l) => lineKey(l) === key);
  const next = lines
    .map((l) => (lineKey(l) === key ? { ...l, quantity: Math.max(0, Math.floor(quantity)) } : l))
    .filter((l) => l.quantity > 0);
  write(next);
  const qty = Math.max(0, Math.floor(quantity));
  if (before && qty > 0 && qty !== before.quantity) {
    track(CIO_EVENTS.CART_UPDATED, {
      product_id: before.productId,
      product_slug: before.slug,
      product_name: before.name,
      variant: before.variantLabel,
      quantity: qty,
      previous_quantity: before.quantity,
      unit_price: before.unitPrice,
      currency: "INR",
    });
  }
}

export function removeLine(productId: string, variantId: string) {
  const existing = read().find((l) => lineKey(l) === `${productId}:${variantId}`);
  updateQuantity(productId, variantId, 0);
  if (existing) {
    track(CIO_EVENTS.REMOVE_FROM_CART, {
      product_id: existing.productId,
      product_slug: existing.slug,
      product_name: existing.name,
      variant: existing.variantLabel,
      quantity: existing.quantity,
      unit_price: existing.unitPrice,
      currency: "INR",
    });
  }
}

export function clearCart() {
  write([]);
}

export function getCart(): CartLine[] {
  return read();
}

export function useCart(): CartLine[] {
  const [lines, setLines] = useState<CartLine[]>([]);
  useEffect(() => {
    const sync = () => setLines(read());
    sync();
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return lines;
}

export function openMiniCart() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_EVT));
}

export function onMiniCartOpen(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(OPEN_EVT, cb);
  return () => window.removeEventListener(OPEN_EVT, cb);
}

export function useCartLineCount(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const sync = () => setN(read().reduce((s, l) => s + l.quantity, 0));
    sync();
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return n;
}

/** A bag line whose saved price was replaced by the current price. */
export type CartPriceChange = { key: string; name: string; variantLabel: string; before: number; after: number };

type PriceSyncResult = {
  productId: string;
  variantId: string;
  status: "ok" | "updated" | "unavailable";
  unitPrice?: number;
  pack?: Pick<PackCartMeta, "bottles" | "subtotal" | "discount" | "addonsTotal">;
};

/**
 * Replace saved bag prices with the server's current prices. Quantities,
 * items and pack contents are untouched. Returns the lines whose price changed.
 */
export function applyCartPriceSync(results: PriceSyncResult[]): CartPriceChange[] {
  const byKey = new Map(results.map((r) => [lineKey(r), r]));
  const changes: CartPriceChange[] = [];
  let dirty = false;
  const next = read().map((line) => {
    const r = byKey.get(lineKey(line));
    if (!r || r.status !== "updated" || typeof r.unitPrice !== "number") return line;
    dirty = true;
    if (Math.round(r.unitPrice * 100) !== Math.round(line.unitPrice * 100)) {
      changes.push({ key: lineKey(line), name: line.name, variantLabel: line.variantLabel, before: line.unitPrice, after: r.unitPrice });
    }
    return {
      ...line,
      unitPrice: r.unitPrice,
      ...(line.pack && r.pack ? { pack: { ...line.pack, ...r.pack } } : {}),
    };
  });
  if (dirty) write(next);
  return changes;
}
